import { Fragment, useState } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import {
  COMPOUND_LIB,
  FOUNDATION_COMPOUNDS,
} from "@/services/mock";
import type { FormulationEntry, CompoundLibEntry } from "@/utils/types/compound";
import type { Target } from "@/utils/types/flavor";
import type { ReplacementPreview } from "@/utils/formulation/computeCurrent";
import { RowReplaceBtn } from "./RowReplaceBtn";
import {
  RowSupplyIcons,
  type SupplyModalKind,
} from "@/components/Modals/Supply/RowSupplyIcons";
import { lookupServerCompound, lookupServerLens } from "@/utils/serverCompounds";
import { chemShort } from "@/utils/lens";
import type { SubstanceSupplyFlags } from "@/utils/types/substance";
import type { AmountUnit } from "@/services/formula_api";
import { normalizeAmountUnit } from "@/utils/formulation/normalizeAmountUnit";
import { ChangeSymbol, type ChangeKind } from "./AutoFormulate/ChangeSymbol";
import { UndoButton } from "./AutoFormulate/UndoButton";
import type { ReplacementBadgeState } from "@/hooks/useReplacementBadge";

export type FlaskRowAIChange = {
  kind: ChangeKind;
  prevPpm?: number;
  newPpm?: number;
};

const ALL_COMPOUNDS = [...COMPOUND_LIB, ...FOUNDATION_COMPOUNDS];
const compoundById = (id: string): CompoundLibEntry | undefined =>
  ALL_COMPOUNDS.find((c) => c.id === id) ?? lookupServerCompound(id);

// Maps the server's FDA SCREAMING_CASE primaryRole onto the unit-cycle
// buckets the FlaskRow's `cycleFor()` already expects (flavoring → ppm/ppb/
// ppt; everything else → mg/g). Mock value names are kept so existing CSS
// + behavior is unchanged.
function unitsRoleFor(primaryRole: string | null | undefined): string {
  if (!primaryRole) return "other";
  if (primaryRole === "NUTRITIVE SWEETENER" || primaryRole === "NON-NUTRITIVE SWEETENER") return "sweetener";
  if (primaryRole.startsWith("FLAVOR")) return "flavoring";
  return "other";
}

// Conc unit cycles + scaling factors. Stored value in ppm-equivalent.
const UNIT_CYCLE_FLAVORING = ["ppm", "ppb", "ppt"] as const;
const UNIT_CYCLE_OTHER = ["mg", "g"] as const;
// "mol%" is a saved unit (AF results) but NOT part of any cycle. Its
// factor of 1 is a placeholder so display/factor lookups don't return
// undefined — but the unit-cycle button is disabled for mol% rows below,
// so this factor is never used for actual conversion math.
export type ConcUnit =
  | (typeof UNIT_CYCLE_FLAVORING)[number]
  | (typeof UNIT_CYCLE_OTHER)[number]
  | "mol%";

const UNIT_TO_PPM: Record<ConcUnit, number> = {
  ppm: 1,
  ppb: 1e-3,
  ppt: 1e-6,
  "mol%": 1,
  mg: 1,
  g: 1000,
};

const defaultUnitFor = (role: string | undefined): ConcUnit =>
  role === "flavoring" ? "ppm" : "mg";

// Trim noisy float artifacts (0.30000000000000004 → "0.3") without forcing
// an integer round — normalization should have already pinned the magnitude
// into [1, 1000), so 4 significant digits is plenty for the input box. Zero
// and integers stay as-is so the column reads cleanly.
function formatDisplayed(v: number): string {
  if (!Number.isFinite(v)) return "0";
  if (v === 0) return "0";
  if (Number.isInteger(v)) return String(v);
  return Number(v.toPrecision(4)).toString();
}
const cycleFor = (role: string | undefined) =>
  role === "flavoring" ? UNIT_CYCLE_FLAVORING : UNIT_CYCLE_OTHER;

export type FlaskRowProps = {
  entry: FormulationEntry;
  isFoundation?: boolean;
  locked?: boolean;
  grandTotal: number;
  showLensTag: boolean;
  unit: ConcUnit | undefined;
  preview: ReplacementPreview | null;
  expanded?: boolean;
  target?: Target | null;
  // Compact mode — drops the Supply/Safety (Info) column so the row fits
  // alongside the Composition aside without forcing horizontal overflow.
  // The column header in FlaskPanel makes the same conditional cut.
  compact?: boolean;
  // Supply flags batch payload + in-flight indicator. Both come from
  // FlaskPanel's useSubstanceSupplyFlags hook. Forwarded to RowSupplyIcons.
  flags?: SubstanceSupplyFlags | null;
  flagsLoading?: boolean;
  onChangeConc: (id: string, value: number, unit?: AmountUnit) => void;
  onCycleUnit: (id: string, role: string | undefined) => void;
  onRemove: (id: string) => void;
  onToggleReplace: (id: string) => void;
  onOpenSupply?: (kind: SupplyModalKind, id: string) => void;
  /** When the Auto-Formulate review banner is showing, every row gets a
   *  leading ChangeSymbol column so symbols align across changed and
   *  unchanged rows. The symbol is empty for unchanged rows. */
  aiReviewing?: boolean;
  /** Non-null when the AI proposed a change to this row. Drives the
   *  prev-value strike-through and replaces the action cell with Undo. */
  aiChange?: FlaskRowAIChange | null;
  /** Called when the user clicks the row's Undo button. */
  onUndoAIChange?: (compoundId: string) => void;
  /** Badge state for the replace button. Derived in FlaskPanel via
   *  useReplacementBadge. Foundation rows pass `{ kind: "disabled" }`
   *  since they don't show the replace button anyway. */
  badge: ReplacementBadgeState;
  /** Optional sublabel/extension text for the replace button's tooltip when
   *  the difficulties dot is on (e.g. "No supplier registered"). */
  badgeDifficultyText?: string;
};

export function FlaskRow({
  entry,
  isFoundation = false,
  locked = false,
  grandTotal,
  showLensTag,
  unit,
  preview,
  expanded = false,
  // target is part of FlaskRowProps so FlaskRowWithBadge can forward it to
  // useReplacementBadge; FlaskRow itself no longer passes it to RowReplaceBtn.
  target: _target,
  compact = false,
  flags,
  flagsLoading,
  onChangeConc,
  onCycleUnit,
  onRemove,
  onToggleReplace,
  onOpenSupply,
  aiReviewing = false,
  aiChange = null,
  onUndoAIChange,
  badge,
  badgeDifficultyText,
}: FlaskRowProps) {
  const c = compoundById(entry.compoundId);
  if (!c) return null;
  const lens = lookupServerLens(entry.compoundId);
  const pct = grandTotal ? (entry.ppm / grandTotal) * 100 : 0;
  const isPreviewing = !!preview && preview.fromId === c.id;
  const isSub = c.kind === "substance";
  const role = unitsRoleFor(lens?.primaryRole);
  // Display-unit priority:
  //   1) `unit` prop — the user's explicit cycle choice (overrides everything).
  //   2) entry.amountUnit — the unit the value was saved in (e.g. mg from Import).
  //      Only honored when it falls within the displayable ConcUnit subset;
  //      kg/μL/mL/L from the broader AmountUnit enum fall through.
  //   3) defaultUnitFor(role) — role-based default (ppm for flavoring, else mg).
  const savedUnitDisplayable: ConcUnit | undefined =
    entry.amountUnit && entry.amountUnit in UNIT_TO_PPM
      ? (entry.amountUnit as ConcUnit)
      : undefined;
  const effectiveUnit = unit || savedUnitDisplayable || defaultUnitFor(role);
  const factor = UNIT_TO_PPM[effectiveUnit] || 1;
  // entry.ppm is the numeric amount; with the unit hint, the conversion math
  // treats it as already-in-effectiveUnit when no cycle override is active so
  // imported non-ppm values display verbatim instead of being mis-scaled.
  const displayed =
    unit && entry.amountUnit && entry.amountUnit !== unit && entry.amountUnit in UNIT_TO_PPM
      ? ((entry.ppm || 0) * UNIT_TO_PPM[entry.amountUnit as ConcUnit]) / factor
      : (entry.ppm || 0);

  // Edit-time local string so the user can type "0." / "1.5" / partial input
  // without each keystroke being parseFloat-round-tripped back through the
  // entry. The displayed-prop only takes over when not editing (draft === null).
  // On blur we normalize and call onChangeConc with the post-normalized
  // (value, unit) — promoting "0.5 ppm" to "500 ppb", "1500 ppb" to "1.5 ppm",
  // etc. (see normalizeAmountUnit).
  const [draft, setDraft] = useState<string | null>(null);
  const inputValue = draft !== null ? draft : formatDisplayed(displayed);
  const commitDraft = () => {
    if (draft === null) return;
    const parsed = parseFloat(draft);
    const safe = Number.isFinite(parsed) ? parsed : 0;
    // The displayed value was computed in `effectiveUnit`; normalize within
    // that chain. The result becomes the new (entry.ppm, entry.amountUnit).
    const norm = normalizeAmountUnit(safe, effectiveUnit as AmountUnit);
    onChangeConc(entry.compoundId, norm.value, norm.unit);
    setDraft(null);
  };

  // Conc input is editable for both Flavor and Fixed-Ingredient rows when
  // the variant isn't locked. Once a batch is started (locked), all rows
  // freeze. Foundation rows still hide the replace/remove actions because
  // those structural edits belong to the spec, not the flask.
  const readOnly = locked;

  // AI-edited rows show the prev value as a strike-through to the LEFT of
  // the conc input. The conc column expands during review so this addition
  // doesn't squeeze the % mix column.
  const aiPrevDisplay =
    aiChange && aiChange.kind === "edit" && aiChange.prevPpm != null
      ? formatDisplayed(
          // Convert the stored ppm-equivalent prev value back into the
          // currently-displayed unit so strike-through reads in the same scale
          // as the live input next to it.
          aiChange.prevPpm / factor,
        )
      : null;

  return (
    <Fragment>
      <div
        className={cn(
          "flask-row",
          isPreviewing && "preview",
          isFoundation && "foundation",
          expanded && !isPreviewing && "expanded",
          aiChange && `af-row-${aiChange.kind}`,
        )}
      >
        {isPreviewing && <span className="preview-stripe" />}
        {/* Leading change-symbol column — only rendered during review. Empty
            cell for unchanged rows so column alignment is preserved. */}
        {aiReviewing && (
          <span
            style={{
              width: 22,
              flexShrink: 0,
              display: "inline-flex",
              justifyContent: "center",
            }}
          >
            <ChangeSymbol kind={aiChange ? aiChange.kind : null} />
          </span>
        )}

        {/* Family stripe — substances (mixtures) get a wider, split treatment
            so they read as visually distinct from single-molecule compounds.
            Mirrors the design used in IngredientSearch's result rows. */}
        <div
          className="fam-stripe"
          style={{
            width: isSub ? 7 : 4,
            display: "flex",
            gap: isSub ? 1 : 0,
            background: "transparent",
            opacity: isPreviewing ? 0.4 : 1,
          }}
        >
          <div
            style={{
              flex: 1,
              borderRadius: 2,
              background: lens?.family?.color ?? "var(--fam-default)",
            }}
          />
          {isSub && (
            <div
              style={{
                flex: 1,
                borderRadius: 2,
                background: lens?.family?.color ?? "var(--fam-default)",
                opacity: 0.45,
              }}
            />
          )}
        </div>

        <div
          className="vstack"
          style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}
        >
          <div className="hstack gap-2" style={{ alignItems: "center", minWidth: 0 }}>
            <span
              className="name"
              style={
                isSub
                  ? {
                      fontStyle: "italic",
                      fontFamily:
                        "var(--font-serif, 'Noto Serif', serif)",
                    }
                  : undefined
              }
            >
              {c.name}
            </span>
            {isPreviewing && preview && (
              <Fragment>
                <span style={{ color: "#d97706", fontSize: 12 }}>→</span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#d97706",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {preview.isDrop ? "(removed)" : preview.replName}
                </span>
                <span className="preview-tag">PREVIEW</span>
              </Fragment>
            )}
            {!isPreviewing && showLensTag && (
              <span className="chem-tag" title={lens?.chemClass ?? undefined}>
                {chemShort(lens?.chemClass)}
              </span>
            )}
          </div>
          <span className="desc">{c.cas}</span>
        </div>

        {/* Conc input + unit cycle. Widened during review so the strike-
            through prev value + current value + unit all fit on one line. */}
        <div
          className="hstack mono"
          style={{
            width: aiReviewing ? 150 : 110,
            gap: 4,
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          {aiPrevDisplay !== null && (
            <span className="af-conc-prev">{aiPrevDisplay}</span>
          )}
          {readOnly ? (
            <Fragment>
              <span
                style={{
                  width: 72,
                  textAlign: "right",
                  padding: "4px 4px",
                  fontSize: 12,
                  color: "var(--ink-3)",
                }}
              >
                {formatDisplayed(displayed)}
              </span>
              <span className="unit-readonly">{effectiveUnit}</span>
            </Fragment>
          ) : (
            <Fragment>
              <input
                type="text"
                inputMode="decimal"
                value={inputValue}
                onChange={(e) => {
                  // Permit digits, one decimal point, and a leading minus.
                  // Anything else (letters, e-notation) is silently rejected so
                  // commitDraft never has to deal with non-numeric input.
                  const v = e.target.value;
                  if (v === "" || /^-?\d*\.?\d*$/.test(v)) setDraft(v);
                }}
                onBlur={commitDraft}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                className="conc-input"
              />
              <button
                type="button"
                onClick={() => onCycleUnit(entry.compoundId, role)}
                // mol% has no cycle peer (conversion math not implemented).
                // Disable so a stray click can't reinterpret the value as
                // ppm/ppb and silently corrupt the saved amount.
                disabled={effectiveUnit === "mol%"}
                title={
                  effectiveUnit === "mol%"
                    ? "mol% — conversion not yet supported"
                    : `Click to cycle units (${cycleFor(role).join(" → ")})`
                }
                className="unit-cycle-btn"
              >
                {effectiveUnit}
              </button>
            </Fragment>
          )}
        </div>

        <span
          className="mono"
          style={{
            width: 50,
            textAlign: "right",
            fontSize: 11,
            color: "var(--ink-3)",
          }}
        >
          {pct.toFixed(1)}%
        </span>

        {!compact && (
          <Fragment>
            <span style={{ width: 16, flexShrink: 0 }} />
            {onOpenSupply ? (
              <RowSupplyIcons
                compoundId={c.id}
                flags={flags ?? null}
                loading={flagsLoading ?? false}
                onOpen={onOpenSupply}
              />
            ) : (
              <span style={{ width: 96 }} />
            )}
            <span style={{ width: 16, flexShrink: 0 }} />
          </Fragment>
        )}

        {isFoundation ? (
          <span style={{ width: 56 }} />
        ) : locked ? null : aiChange && onUndoAIChange ? (
          // Review mode, this row has a proposed change — replace
          // replace/× with Undo so the user can dial back this single
          // change without rejecting the whole run.
          <div
            className="hstack af-undo-cell"
            style={{ gap: 2, width: 56, justifyContent: "flex-end" }}
          >
            <UndoButton
              kind={aiChange.kind}
              onClick={() => onUndoAIChange(entry.compoundId)}
            />
          </div>
        ) : (
          <div
            className="hstack"
            style={{ gap: 2, width: 56, justifyContent: "flex-end" }}
          >
            <RowReplaceBtn
              compoundId={c.id}
              badge={badge}
              expanded={expanded}
              onClick={onToggleReplace}
              difficultyText={badgeDifficultyText}
            />
            <button
              type="button"
              className="btn xs ghost"
              onClick={() => onRemove(entry.compoundId)}
              style={{ padding: "3px 5px" }}
            >
              <Icon name="x" size={12} />
            </button>
          </div>
        )}
      </div>
    </Fragment>
  );
}
